/** @jsxImportSource @emotion/react */
import React from 'react';
import styled from '@emotion/styled';
import { ChevronDown, ChevronUp } from 'lucide-react';
import * as color from '@constant/color';
import { ETheme } from '@datatype/type';
import {
  IAdminStudentMetricData,
  TAdminLanguageLevel,
  TAdminSourceChannel,
  TAdminStudyMotivation,
  TAdminStudyTimeGoal,
} from '@datatype/admin/api-type';
import TextBlock from '@component/common/block/text-block';
import Chip from '@design-system/chip';

interface IProps {
  student: IAdminStudentMetricData;
}

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 8px;
  padding: 12px 14px;
  border: 1px solid ${color.GRAY_3};
  background-color: ${color.WHITE_1};
  border-radius: 8px;
`;

const TopRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
`;

const Identity = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const LanguageBar = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const LanguageGroupBox = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: flex-start;
  gap: 8px;
`;

const LanguageBadge = styled.div`
  min-width: 28px;
  padding: 2px 6px;
  background-color: ${color.GRAY_4};
  border-radius: 4px;
  text-align: center;
`;

const Divider = styled.div`
  align-self: stretch;
  height: 1px;
  background-color: ${color.GRAY_3};
`;

const ChipRow = styled.div`
  display: flex;
  flex-flow: row wrap;
  gap: 4px;
  align-items: center;
`;

const LanguageContent = styled.div`
  display: flex;
  flex-flow: column nowrap;
  gap: 4px;
  min-width: 0;
`;

const StudyMetricBox = styled.div<{ $themeColor: string }>`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  gap: 2px;
  padding: 1px 3px;
  border: 1px solid ${({ $themeColor }) => $themeColor};
  border-radius: 4px;
  color: ${({ $themeColor }) => $themeColor};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
`;

const AttendanceBadge = styled.span<{ $backgroundColor: string; $textColor: string }>`
  padding: 0 2px;
  border-radius: 2px;
  background-color: ${({ $backgroundColor }) => $backgroundColor};
  color: ${({ $textColor }) => $textColor};
`;

interface IStudyMetric {
  text: string;
  attendanceCount: number;
  hasStudy: boolean;
}

interface IStudyMetricChipProps {
  study: IStudyMetric;
}

const StudyMetricChip: React.FC<IStudyMetricChipProps> = ({ study }) => {
  const isInactive = !study.hasStudy;
  const studyColor = isInactive ? color.GRAY_3 : color.BLUE_2;
  const attendanceColor = isInactive ? color.GRAY_3 : color.BLUE_2;

  return (
    <StudyMetricBox $themeColor={studyColor}>
      <span>{study.text}</span>
      <AttendanceBadge $backgroundColor={isInactive ? studyColor : attendanceColor} $textColor={color.WHITE_1}>
        {study.attendanceCount}회
      </AttendanceBadge>
    </StudyMetricBox>
  );
};

const formatStudyTime = (ms: number): string => {
  if (!ms || ms <= 0) return '0초';
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes === 0) return `${seconds}초`;
  return `${minutes}분 ${seconds}초`;
};

const formatCreatedDate = (iso: string | null): string => {
  if (!iso) return '';
  return iso.slice(0, 10);
};

const formatStudyClock = (iso: string | null): string | null => {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  // 백엔드가 Asia/Seoul로 직렬화한 ZonedDateTime — 브라우저 로컬 표시는 사용자 환경 시각으로 fallback 가능하지만
  // 어드민은 KST 운영이므로 KST 그대로 표시.
  const formatter = new Intl.DateTimeFormat('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'Asia/Seoul',
  });
  return formatter.format(date);
};

const SOURCE_CHANNEL_LABELS: Record<TAdminSourceChannel, string> = {
  SNS: 'SNS',
  STORE: '스토어 검색',
  SEARCH: '인터넷 검색',
  ARTICLE: '블로그·커뮤니티',
  REFERRAL: '지인 추천',
  ETC: '기타',
};

const STUDY_MOTIVATION_LABELS: Record<TAdminStudyMotivation, string> = {
  HOBBY: '취미',
  CONVERSATION: '회화',
  STUDY: '학업',
  WORK: '업무',
  ETC: '기타',
};

const LANGUAGE_LEVEL_LABELS: Record<TAdminLanguageLevel, string> = {
  BEGINNER: '입문',
  BASIC: '기초',
  ELEMENTARY: '초급',
  INTERMEDIATE: '중급',
  ADVANCED: '고급',
};

const STUDY_TIME_GOAL_LABELS: Record<TAdminStudyTimeGoal, string> = {
  MINIMAL: '5분',
  MEDIUM: '10분',
  CONSIDERABLE: '20분',
  MAXIMAL: '30분 이상',
};

const toDateString = (date: Date): string => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const buildRecentSevenDays = (): string[] => {
  const today = new Date();
  const days: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    days.push(toDateString(d));
  }
  return days;
};

interface IStudyDotProps {
  $state: 'today-attended' | 'today-absent' | 'attended' | 'absent' | 'before-signup';
}

const createDotColors = (state: IStudyDotProps['$state']) => {
  switch (state) {
    case 'today-attended':
      return { bg: color.GREEN_4, border: color.YELLOW_1 };
    case 'today-absent':
      return { bg: color.WHITE_1, border: color.YELLOW_1 };
    case 'attended':
      return { bg: color.GREEN_4, border: color.GREEN_4 };
    case 'absent':
      return { bg: color.WHITE_1, border: color.GRAY_2 };
    case 'before-signup':
    default:
      return { bg: color.GRAY_4, border: color.GRAY_4 };
  }
};

const StudyDot = styled.div<IStudyDotProps>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  box-sizing: border-box;
  background-color: ${(p) => createDotColors(p.$state).bg};
  border: 1px solid ${(p) => createDotColors(p.$state).border};
`;

const AttendanceRow = styled.div`
  display: flex;
  flex-flow: row nowrap;
  gap: 3px;
  align-items: center;
`;

const AttendanceTooltipTrigger = styled.div`
  position: relative;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
`;

const AttendanceChipButton = styled.button<{ $hasAttendance: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  padding: 1px 3px;
  border: 1px solid ${({ $hasAttendance }) => ($hasAttendance ? color.GREEN_3 : color.GREEN_5)};
  border-radius: 5px;
  background-color: ${({ $hasAttendance }) => ($hasAttendance ? color.GREEN_3 : color.GREEN_5)};
  color: ${color.WHITE_1};
  font-size: 12px;
  font-weight: 600;
  line-height: 16px;
  cursor: pointer;

  &:hover {
    filter: brightness(0.9);
  }
`;

const AttendanceTooltip = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 10;
  max-height: 220px;
  min-width: 110px;
  overflow-y: auto;
  padding: 8px 10px;
  border-radius: 6px;
  background-color: ${color.BLACK_2};
  color: ${color.WHITE_1};
  font-size: 12px;
  line-height: 18px;
  white-space: pre-line;
  visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
  opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
  transition: opacity 120ms ease;
  pointer-events: ${({ $isOpen }) => ($isOpen ? 'auto' : 'none')};
`;

interface ILanguageGroupProps {
  label: string;
  expressionTime: number;
  expressionCount: number;
  storyTime: number;
  storyCount: number;
  characterTime: number;
  characterCount: number;
  classroomGrammarTime: number;
  classroomGrammarCount: number;
  speakingTime?: number;
  worksheetTime?: number;
  speakingCount?: number;
  worksheetCount?: number;
  attendanceCounts?: Record<string, number>;
  basicExpressionTime?: number;
  basicExpressionCount?: number;
  practicalConversationTime?: number;
  practicalConversationCount?: number;
  vocabularyBookTime: number;
  vocabularyBookCount: number;
  vocabularyTime: number;
  grammarTime: number;
  vocabularyCount: number;
  grammarCount: number;
  studyMotivation: TAdminStudyMotivation[];
  languageLevel: TAdminLanguageLevel | null;
  studyTimeGoal: TAdminStudyTimeGoal | null;
}

const LanguageGroup: React.FC<ILanguageGroupProps> = ({
  label,
  expressionTime,
  expressionCount,
  storyTime,
  storyCount,
  characterTime,
  characterCount,
  classroomGrammarTime,
  classroomGrammarCount,
  speakingTime,
  worksheetTime,
  speakingCount,
  worksheetCount,
  attendanceCounts,
  basicExpressionTime,
  basicExpressionCount,
  practicalConversationTime,
  practicalConversationCount,
  vocabularyBookTime,
  vocabularyBookCount,
  vocabularyTime,
  grammarTime,
  vocabularyCount,
  grammarCount,
  studyMotivation,
  languageLevel,
  studyTimeGoal,
}) => {
  // 백엔드 미배포 시점 등에서 신규 필드가 undefined일 수 있어 0으로 정규화
  // attendanceType은 여러 개를 받을 수 있다 (테이블 리네임으로 구/신 enum이 공존하는 생활회화 등)
  const createStudyChip = (
    label: string,
    count: number | undefined,
    time: number | undefined,
    ...attendanceTypes: string[]
  ) => {
    const normalizedCount = count ?? 0;
    const normalizedTime = time ?? 0;
    const attendanceCount = attendanceTypes.reduce((sum, type) => sum + (attendanceCounts?.[type] ?? 0), 0);
    const hasStudy = normalizedCount > 0 || normalizedTime > 0;
    return {
      text: hasStudy ? `${label} ${normalizedCount}개 / ${formatStudyTime(normalizedTime)}` : `${label} X`,
      attendanceCount,
      hasStudy,
    };
  };
  const expressionStudy = createStudyChip(
    '생생표현',
    expressionCount,
    expressionTime,
    label === 'EN' ? 'ENGLISH_AUTHENTIC_EXPRESSION' : 'JAPANESE_AUTHENTIC_EXPRESSION',
  );
  const storyStudy = createStudyChip(
    '간편이야기',
    storyCount,
    storyTime,
    label === 'EN' ? 'ENGLISH_STORY' : 'JAPANESE_STORY',
  );
  const characterStudy = createStudyChip(
    '글자',
    characterCount,
    characterTime,
    label === 'EN' ? 'ENGLISH_CLASSROOM_CHARACTER' : 'JAPANESE_CLASSROOM_CHARACTER',
  );
  const classroomGrammarStudy = createStudyChip(
    '교실문법',
    classroomGrammarCount,
    classroomGrammarTime,
    label === 'EN' ? 'ENGLISH_CLASSROOM_GRAMMAR' : 'JAPANESE_CLASSROOM_GRAMMAR',
  );
  const worksheetStudy = createStudyChip('학습지', worksheetCount, worksheetTime);
  const speakingStudyBase = createStudyChip(
    'AI 말하기',
    speakingCount,
    speakingTime,
    label === 'EN' ? 'ENGLISH_SPEAKING' : 'JAPANESE_SPEAKING',
  );
  const speakingStudy = {
    ...speakingStudyBase,
    text: speakingStudyBase.hasStudy
      ? `AI 말하기 ${speakingCount ?? 0}턴 / ${formatStudyTime(speakingTime ?? 0)}`
      : 'AI 말하기 X',
  };
  const basicExpressionStudy =
    basicExpressionTime == null && basicExpressionCount == null
      ? null
      : createStudyChip(
          '기본표현',
          basicExpressionCount ?? 0,
          basicExpressionTime ?? 0,
          'JAPANESE_CLASSROOM_EXPRESSION',
        );
  const practicalConversationStudy =
    practicalConversationTime == null && practicalConversationCount == null
      ? null
      : createStudyChip(
          '생활회화',
          practicalConversationCount ?? 0,
          practicalConversationTime ?? 0,
          'JAPANESE_CLASSROOM_CONVERSATION',
          'JAPANESE_CLASSROOM_PRACTICAL_CONVERSATION', // 구 enum (리네임 이전 기록)
        );
  const vocabularyBookStudy = createStudyChip(
    '단어집',
    vocabularyBookCount,
    vocabularyBookTime,
    label === 'EN' ? 'ENGLISH_VOCABULARY_BOOK' : 'JAPANESE_VOCABULARY_BOOK',
  );
  const vocabularyClozeStudy = createStudyChip(
    '빈칸문제(어휘)',
    vocabularyCount,
    vocabularyTime,
    label === 'EN' ? 'ENGLISH_CLOZE_VOCABULARY' : 'JAPANESE_CLOZE_VOCABULARY',
    label === 'EN' ? 'ENGLISH_VOCABULARY' : 'JAPANESE_VOCABULARY', // 구 enum (CLOZE_ 접두사 이전 기록)
  );
  const grammarClozeStudy = createStudyChip(
    '빈칸문제(문법)',
    grammarCount,
    grammarTime,
    label === 'EN' ? 'ENGLISH_CLOZE_GRAMMAR' : 'JAPANESE_CLOZE_GRAMMAR',
    label === 'EN' ? 'ENGLISH_GRAMMAR' : 'JAPANESE_GRAMMAR', // 구 enum (CLOZE_ 접두사 이전 기록)
  );

  return (
    <LanguageGroupBox>
      <LanguageBadge>
        <TextBlock text={label} design={{ text: { size: 'tiny', color: color.BLACK_5, bold: true } }} />
      </LanguageBadge>
      <LanguageContent>
        <ChipRow>
          {studyMotivation?.length > 0 && (
            <Chip
              text={`목적:${studyMotivation.map((motivation) => STUDY_MOTIVATION_LABELS[motivation]).join('·')}`}
              size='tiny'
              kind='filled'
              isSelected
              theme={ETheme.BROWN}
            />
          )}
          {languageLevel && (
            <Chip
              text={`학습단계:${LANGUAGE_LEVEL_LABELS[languageLevel]}`}
              size='tiny'
              kind='filled'
              isSelected
              theme={ETheme.BROWN}
            />
          )}
          {studyTimeGoal && (
            <Chip
              text={`목표시간:${STUDY_TIME_GOAL_LABELS[studyTimeGoal]}`}
              size='tiny'
              kind='filled'
              isSelected
              theme={ETheme.BROWN}
            />
          )}
        </ChipRow>
        <ChipRow>
          <StudyMetricChip study={expressionStudy} />
          <StudyMetricChip study={storyStudy} />
          <StudyMetricChip study={characterStudy} />
          <StudyMetricChip study={classroomGrammarStudy} />
          <StudyMetricChip study={speakingStudy} />
          <StudyMetricChip study={worksheetStudy} />
          {basicExpressionStudy && <StudyMetricChip study={basicExpressionStudy} />}
          {practicalConversationStudy && <StudyMetricChip study={practicalConversationStudy} />}
          <StudyMetricChip study={vocabularyBookStudy} />
          <StudyMetricChip study={vocabularyClozeStudy} />
          <StudyMetricChip study={grammarClozeStudy} />
        </ChipRow>
      </LanguageContent>
    </LanguageGroupBox>
  );
};

const createMembershipChip = (
  isMember: boolean,
  subscriptionPlan: IAdminStudentMetricData['subscriptionPlan'],
): { text: string; theme: ETheme; tone: 'strong' | 'light' } => {
  if (!isMember) return { text: '비회원', theme: ETheme.GRAY, tone: 'strong' };
  if (subscriptionPlan === 'MONTHLY_PLAN') return { text: '월간회원', theme: ETheme.BROWN, tone: 'strong' };
  if (subscriptionPlan === 'YEARLY_PLAN') return { text: '연간회원', theme: ETheme.BROWN, tone: 'strong' };
  return { text: '무료회원', theme: ETheme.BROWN, tone: 'light' };
};

const StudentItem: React.FC<IProps> = ({ student }) => {
  const [isAttendanceOpen, setIsAttendanceOpen] = React.useState(false);
  const isMember = student.userId != null;
  const displayName = student.username ?? student.email ?? student.studentKey ?? '-';
  const membershipChip = createMembershipChip(isMember, student.subscriptionPlan);
  const recentStudiedDates = student.recentStudiedDates ?? student.recentAttendedDates ?? [];
  const attendanceDates = student.attendanceDates ?? [];
  const recentAttendanceTooltip = attendanceDates.length
    ? `출석일\n${attendanceDates.map((date) => date.replaceAll('-', '.')).join('\n')}`
    : '출석 기록 없음';

  return (
    <Wrapper>
      <TopRow>
        <Identity>
          <Chip
            text={membershipChip.text}
            size='tiny'
            isSelected
            theme={membershipChip.theme}
            tone={membershipChip.tone}
          />
          <TextBlock
            text={displayName}
            design={{ text: { size: 'medium', color: color.BLACK_2, bold: true, oneline: true } }}
          />
          {isMember && student.email && (
            <TextBlock text={student.email} design={{ text: { size: 'small', color: color.BLACK_5, oneline: true } }} />
          )}
          <AttendanceRow>
            {(() => {
              const days = buildRecentSevenDays();
              const todayStr = days[days.length - 1];
              return days.map((dateStr) => {
                const isAttended = recentStudiedDates.includes(dateStr);
                const isBeforeSignup = !!student.studentCreatedAt && dateStr < student.studentCreatedAt.slice(0, 10);
                const isToday = dateStr === todayStr;
                const state: IStudyDotProps['$state'] = isBeforeSignup
                  ? 'before-signup'
                  : isToday
                    ? isAttended
                      ? 'today-attended'
                      : 'today-absent'
                    : isAttended
                      ? 'attended'
                      : 'absent';
                return <StudyDot key={dateStr} $state={state} title={dateStr} />;
              });
            })()}
          </AttendanceRow>
          <AttendanceTooltipTrigger>
            <AttendanceChipButton
              type='button'
              $hasAttendance={(student.attendanceCount ?? 0) > 0}
              aria-label={isAttendanceOpen ? '출석일 목록 닫기' : '출석일 목록 보기'}
              aria-expanded={isAttendanceOpen}
              onClick={() => setIsAttendanceOpen((isOpen) => !isOpen)}>
              출석 {student.attendanceCount ?? 0}회
              {isAttendanceOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </AttendanceChipButton>
            <AttendanceTooltip $isOpen={isAttendanceOpen}>{recentAttendanceTooltip}</AttendanceTooltip>
          </AttendanceTooltipTrigger>
          {student.studentCreatedAt && (
            <Chip
              text={`생성 ${formatCreatedDate(student.studentCreatedAt)}`}
              size='tiny'
              kind='filled'
              isSelected
              theme={ETheme.GRAY}
            />
          )}
          {student.sourceChannel && (
            <Chip
              text={`유입:${SOURCE_CHANNEL_LABELS[student.sourceChannel]}`}
              size='tiny'
              kind='filled'
              isSelected
              theme={ETheme.BROWN}
            />
          )}
          {student.attendancePlatform && (
            <Chip text={student.attendancePlatform} size='tiny' kind='filled' isSelected theme={ETheme.BLUE} />
          )}
          {(() => {
            const first = formatStudyClock(student.firstStudiedAt);
            const last = formatStudyClock(student.lastStudiedAt);
            if (!first && !last) return null;
            return (
              <Chip
                text={`학습 ${first ?? last} ~ ${last ?? first}`}
                size='tiny'
                kind='filled'
                isSelected
                theme={ETheme.YELLOW}
              />
            );
          })()}
          {isMember && (
            <Chip
              text={`${student.pushPlatform ?? '-'} → 푸시 ${student.hasPushToken ? 'O' : 'X'}`}
              size='tiny'
              kind='filled'
              isSelected
              theme={ETheme.BLUE}
            />
          )}
          {/* TODO: 노트 지표 정의 확정 전까지 임시 숨김
          <Chip
            text={`빈칸문제저장 ${student.bookmarkCount}`}
            size='tiny'
            kind='filled'
            isSelected
            theme={ETheme.BROWN}
            tone={student.bookmarkCount > 0 ? 'strong' : 'light'}
          />
          <Chip
            text={`빈칸문제반복 ${student.bookmarkRepeatCount}`}
            size='tiny'
            kind='filled'
            isSelected
            theme={ETheme.BROWN}
            tone={student.bookmarkRepeatCount > 0 ? 'strong' : 'light'}
          />
          <Chip
            text={`틀린문제저장 ${student.incorrectCount}`}
            size='tiny'
            kind='filled'
            isSelected
            theme={ETheme.BROWN}
            tone={student.incorrectCount > 0 ? 'strong' : 'light'}
          />
          <Chip
            text={`틀린문제반복 ${student.incorrectRepeatCount}`}
            size='tiny'
            kind='filled'
            isSelected
            theme={ETheme.BROWN}
            tone={student.incorrectRepeatCount > 0 ? 'strong' : 'light'}
          />
          */}
        </Identity>
      </TopRow>
      <LanguageBar>
        <LanguageGroup
          label='EN'
          expressionTime={student.englishAuthenticExpressionStudyTime}
          expressionCount={student.englishAuthenticExpressionStudyCount}
          storyTime={student.englishMiniStoryStudyTime}
          storyCount={student.englishMiniStoryStudyCount}
          characterTime={student.englishCharacterStudyTime}
          characterCount={student.englishCharacterStudyCount}
          classroomGrammarTime={student.englishClassroomGrammarStudyTime}
          classroomGrammarCount={student.englishClassroomGrammarStudyCount}
          speakingTime={student.englishSpeakingStudyTime}
          speakingCount={student.englishSpeakingStudyCount}
          worksheetTime={student.englishWorksheetStudyTime}
          worksheetCount={student.englishWorksheetStudyCount}
          attendanceCounts={student.studyAttendanceCount}
          vocabularyBookTime={student.englishVocabularyBookStudyTime}
          vocabularyBookCount={student.englishStudyVocabularyBookStudyCount}
          vocabularyTime={student.englishVocabularyStudyTime}
          grammarTime={student.englishGrammarStudyTime}
          vocabularyCount={student.englishVocabularyProblemCount}
          grammarCount={student.englishGrammarProblemCount}
          studyMotivation={student.englishStudyMotivation}
          languageLevel={student.englishLanguageLevel}
          studyTimeGoal={student.englishStudyTimeGoal}
        />
        <Divider />
        <LanguageGroup
          label='JA'
          expressionTime={student.japaneseAuthenticExpressionStudyTime}
          expressionCount={student.japaneseAuthenticExpressionStudyCount}
          storyTime={student.japaneseMiniStoryStudyTime}
          storyCount={student.japaneseMiniStoryStudyCount}
          characterTime={student.japaneseCharacterStudyTime}
          characterCount={student.japaneseCharacterStudyCount}
          classroomGrammarTime={student.japaneseClassroomGrammarStudyTime}
          classroomGrammarCount={student.japaneseClassroomGrammarStudyCount}
          speakingTime={student.japaneseSpeakingStudyTime}
          speakingCount={student.japaneseSpeakingStudyCount}
          worksheetTime={student.japaneseWorksheetStudyTime}
          worksheetCount={student.japaneseWorksheetStudyCount}
          attendanceCounts={student.studyAttendanceCount}
          basicExpressionTime={student.japaneseBasicExpressionStudyTime}
          basicExpressionCount={student.japaneseBasicExpressionStudyCount}
          practicalConversationTime={student.japanesePracticalConversationStudyTime}
          practicalConversationCount={student.japanesePracticalConversationStudyCount}
          vocabularyBookTime={student.japaneseVocabularyBookStudyTime}
          vocabularyBookCount={student.japaneseStudyVocabularyBookStudyCount}
          vocabularyTime={student.japaneseVocabularyStudyTime}
          grammarTime={student.japaneseGrammarStudyTime}
          vocabularyCount={student.japaneseVocabularyProblemCount}
          grammarCount={student.japaneseGrammarProblemCount}
          studyMotivation={student.japaneseStudyMotivation}
          languageLevel={student.japaneseLanguageLevel}
          studyTimeGoal={student.japaneseStudyTimeGoal}
        />
      </LanguageBar>
    </Wrapper>
  );
};

export default StudentItem;
